import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, createSeriesMarkers, LineStyle, CrosshairMode } from 'lightweight-charts';
import { MatDialog } from '@angular/material/dialog';
import { SseService } from 'src/app/services/SseService';
import { TemaService } from 'src/app/services/tema.services';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom, Subscription } from 'rxjs';
import { co } from '@fullcalendar/core/internal-common';
import { MetaApiRealTimeWsService, MetaApiPrice } from 'src/app/services/metaapi-realtime-ws.service';
import { MetaApiStreamingService } from 'src/app/services/metaapi-streaming.service';
import { MetaapiCandlesSyncService } from 'src/app/services/metaapi-candles-sync.service';
import { ChartDrawingController } from '../../common/chart-drawing/chart-drawing-controller';
import { fitOverlayToPriceScale } from '../../common/chart-drawing/offscale-line';
import { getToolLabel } from '../../common/chart-drawing/drawing-tools.catalog';
import { DrawingTextDialogComponent } from '../../common/chart-drawing/drawing-text-dialog/drawing-text-dialog.component';
import { CrosshairOhlc, LegendItem } from '../../common/chart-core/chart-legend/chart-legend.component';
import { OSCILLATORS } from '../../common/chart-core/oscillators.const';
import { INDICATOR_CATALOG, IndCatalogEntry } from '../../common/chart-core/indicator-catalog.const';
import { getChartColors } from '../../common/chart-core/chart-colors';
import { RANGES, TIMEFRAME_OPTIONS, isPeriodoIntraday, rangeFromMs } from '../../common/chart-core/timeframes.const';
import { castEntryConditions, splitCondicionLegible } from '../../common/chart-core/cast-conditions';
import { OpenTradeLevels, applyOpenTradeLines } from '../../common/chart-core/open-trade-lines';

export interface ChartItem {
  symbol: string;
  label: string;
  idModelo?: string;
  idCarteraOptima?: string;
  periodo?: number;
  fechaRegistroSuscripcion?: string;
  suscripcion?: any;
  metaapi?: boolean;
  availableAssets?: any[];
}

@Component({
  selector: 'app-multicharts-dialog',
  templateUrl: './multicharts-dialog.component.html',
  styleUrls: ['./multicharts-dialog.component.scss']
})
export class MultichartsDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('chartContainer') chartContainers!: QueryList<ElementRef<HTMLDivElement>>;
  @Input() data: { charts: ChartItem[] } = { charts: [] };
  @Output() closed = new EventEmitter<void>();
  @Output() operationHovered = new EventEmitter<string[]>();
  isos: string[] = [];
  private charts: any[] = [];
  // metadata per chart (candle series, volume series, drawing controller, leyenda, indicator series...)
  chartMeta: Array<any> = [];
  private themeSubscription?: Subscription;
  private subRealTimeWs: Subscription | null = null;
  /** Sondeo de localStorage mientras el socket de MetaAPI está caído. */
  private subRealTimeFallback: Subscription | null = null;
  private subStreamStatus?: Subscription;
  /** Símbolos suscritos ahora mismo al socket (en mayúsculas, ordenados), para no resuscribir sin cambios. */
  private streamSymbolsKey = '';
  private subVelasSincronizadas?: Subscription;
  /** Última vez que el broker persistió velas MetaAPI: los indicadores pedidos antes se quedan sin ellas. */
  private ultimaSincronizacionMs = 0;
  isDark: boolean = false;
  loadingMap: { [index: number]: boolean } = {};
  opsCountMap: { [symbol: string]: number } = {};
  /** Live bid prices for MetaAPI symbols — shown as badge in the header */
  livePrices: { [symbol: string]: MetaApiPrice } = {};
  private token: string = '';
  maximizedIndex: number | null = null;
  /** Map from uppercase symbol to ALL chart indices that display it */
  private symbolIndexMap: { [symbol: string]: number[] } = {};
  private candleRefreshTimers: Array<ReturnType<typeof setTimeout> | null> = [];
  private destroyed = false;
  private readonly candleCachePrefix = 'nec_multichart_candles_v1';
  // Multicharts es para mirar indicadores/valores de los últimos períodos, no
  // para scrollear histórico largo: se carga, cachea y muestra solo esta
  // cantidad de barras por chart (cache, merge de refresh e histórico inicial).
  private readonly maxCandlesInCache = 300;
  /** Velas pedidas al abrir el chart (`limite` del endpoint): las N más recientes. */
  private readonly initialCandles = 100;
  /** Velas pedidas cada vez que el scroll llega al borde izquierdo de lo cargado. */
  private readonly olderChunkCandles = 100;
  /** Margen (en barras) al borde izquierdo a partir del cual se piden velas anteriores. */
  private readonly olderLoadThresholdBars = 10;
  /** Tope duro de chunks encadenados que setChartRange() puede pedir antes de rendirse (backstop). */
  private readonly maxRangeLoadChunks = 100;
  /** Tope de historial que setChartRange('All') carga para timeframes intradía (~2 años); D/W no tienen tope práctico. */
  private readonly rangeLoadIntradayCapMs = 730 * 24 * 60 * 60 * 1000;
  /** Barras visibles por defecto al cargar el chart o maximizarlo. */
  private readonly defaultVisibleBars = 50;
  /** Margen tras el cierre de vela antes de pedirla al backend (tiempo para que la consolide). */
  private readonly refreshGraceMs = 3000;
  /** Tope entre refrescos de velas: en D/W evita esperar un período entero si el grid se desalinea. */
  private readonly maxRefreshDelayMs = 15 * 60_000;
  /** Throttle del recálculo de indicadores de catálogo con los ticks en vivo. */
  private readonly liveIndicatorThrottleMs = 1000;

  private readonly oscillators: string[] = OSCILLATORS;

  private readonly indicatorPalette: string[] = [
    '#2196F3','#FF9800','#9C27B0','#00BCD4','#CDDC39',
    '#F44336','#4CAF50','#FF5722','#607D8B','#E91E63',
    '#3F51B5','#009688','#FFC107','#795548','#8BC34A',
    '#03A9F4','#FF4081','#69F0AE','#EA80FC','#FFD740',
  ];

  // ── Fase 2: UI pesada (catálogo de indicadores, timeframe/rangos) — solo se
  // muestra en la celda maximizada, pero el estado del panel (búsqueda/abierto)
  // es único porque solo una celda puede estar maximizada a la vez.
  public readonly indicatorCatalog: IndCatalogEntry[] = INDICATOR_CATALOG;
  public showIndicatorsPanel: boolean = false;
  public indicatorSearch: string = '';
  public readonly timeframeOptions = TIMEFRAME_OPTIONS;
  public readonly ranges: string[] = RANGES;

  public getPeriodoLabel(periodo?: number): string {
    return this.timeframeOptions.find(tf => tf.periodo === (periodo ?? 6))?.label ?? '';
  }

  get filteredIndicatorCatalog(): IndCatalogEntry[] {
    const search = this.indicatorSearch.trim().toLowerCase();
    if (!search) return this.indicatorCatalog;
    return this.indicatorCatalog.filter(e =>
      e.title.toLowerCase().includes(search) || e.short.toLowerCase().includes(search)
    );
  }

  toggleIndicatorsPanel(): void {
    this.showIndicatorsPanel = !this.showIndicatorsPanel;
  }

  /** Igual que "periodoBloqueado" en grafica-modelo: si el chart viene de un modelo suscrito,
   *  el periodo/timeframe no se puede cambiar libremente (lo define el modelo). */
  isChartLocked(index: number): boolean {
    return !!this.data.charts[index]?.idModelo;
  }

  get gridColumns(): number {
    return this.data.charts.length;
  }

  get chartHeight(): number {
    //const n = this.data.charts.length;
//    return n <= 3 ? 440 : 300;
    return 300;
  }

  constructor(
    private _sseService: SseService,
    private temaService: TemaService,
    private cdr: ChangeDetectorRef,
    private _cookieService: CookieService,
    private metaApiRealTimeWs: MetaApiRealTimeWsService,
    private metaApiStreaming: MetaApiStreamingService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private metaapiCandlesSync: MetaapiCandlesSyncService
  ) {}

  ngAfterViewInit(): void {
    this.token = this._cookieService.get('auth_token');
    this.themeSubscription = this.temaService.valDarkTheme.subscribe(isDark => {
      this.isDark = isDark;
      this.applyThemeToCharts();
    });
    this.subVelasSincronizadas = this.metaapiCandlesSync.velasSincronizadas$
      .subscribe(() => {
        this.ultimaSincronizacionMs = Date.now();
        this.refreshMetaapiChartsNow();
      });
    // Sin esto la BD llega solo hasta el último cierre sincronizado y queda un hueco hasta la vela en vivo.
    if (this.data.charts.some(c => c.metaapi)) this.metaapiCandlesSync.solicitarSincronizacion();
    setTimeout(() => this.initCharts(), 100);
  }

  private async initCharts(): Promise<void> {
    const containers = this.chartContainers.toArray();
    console.log('Initializing charts for symbols:', this.data.charts);
    for (let i = 0; i < containers.length; i++) {
      await this.createChartForSymbol(containers[i].nativeElement, this.data.charts[i], i);
      const sym = this.data.charts[i].symbol.toUpperCase();
      if (!this.symbolIndexMap[sym]) this.symbolIndexMap[sym] = [];
      this.symbolIndexMap[sym].push(i);
    }
    this.connectMetaApiRealTime();
    this.subStreamStatus = this.metaApiStreaming.status$.subscribe(status => {
      if (status === 'down') this.startRealTimeFallback();
      else if (status === 'synced') this.stopRealTimeFallback();
    });
  }

  /**
   * Símbolos MetaAPI que tienen al menos una gráfica, sin repetir: varias gráficas del mismo activo
   * comparten una suscripción y applyLiveTick reparte el tick a todas vía symbolIndexMap.
   * Las gráficas marcadas metaapi:false no se suscriben; las que no traen el flag, solo si su
   * símbolo está entre los isos del mercado.
   */
  private activeStreamSymbols(): string[] {
    const isosUpper = new Set(this.isos.map(i => (i || '').toUpperCase()));
    const bySymbol = new Map<string, string>();
    for (const c of this.data.charts) {
      const symbol = (c?.symbol || '').trim();
      if (!symbol) continue;
      const upper = symbol.toUpperCase();
      const isMetaapi = c.metaapi === true || (c.metaapi === undefined && isosUpper.has(upper));
      if (isMetaapi && !bySymbol.has(upper)) bySymbol.set(upper, symbol);
    }
    return Array.from(bySymbol.values());
  }

  /** (Re)suscribe al socket de MetaAPI solo si cambió el conjunto de símbolos con gráfica. */
  private connectMetaApiRealTime(): void {
    const symbols = this.activeStreamSymbols();
    const key = symbols.map(s => s.toUpperCase()).sort().join(',');
    if (key === this.streamSymbolsKey && this.subRealTimeWs) return;
    this.streamSymbolsKey = key;

    // Se abre la nueva suscripción antes de soltar la anterior: los símbolos que siguen no llegan
    // a contador 0 y no se desuscriben/resuscriben en MetaAPI.
    const previous = this.subRealTimeWs;
    this.subRealTimeWs = symbols.length
      ? this.metaApiStreaming.prices$(symbols).subscribe(price => this.applyLiveTick(price))
      : null;
    previous?.unsubscribe();

    if (this.subRealTimeFallback) this.startRealTimeFallback();
  }

  /** Mientras el socket está caído, se vuelve a leer lo que otros escriben en localStorage. */
  private startRealTimeFallback(): void {
    this.stopRealTimeFallback();
    const symbols = this.activeStreamSymbols();
    if (!symbols.length) return;
    this.subRealTimeFallback = this.metaApiRealTimeWs.pollLocalPrices(symbols, 2000).subscribe({
      next: (price: MetaApiPrice) => this.applyLiveTick(price)
    });
  }

  private stopRealTimeFallback(): void {
    this.subRealTimeFallback?.unsubscribe();
    this.subRealTimeFallback = null;
  }

  /** Hora del broker menos UTC real, en segundos; se aprende del brokerTime de los ticks. */
  private brokerOffsetSec: number | null = null;

  /**
   * "Ahora" en la misma base que el `time` de las velas: hora del broker tratada como UTC, porque
   * tbl_historicos_metaapi.fecha es TIMESTAMP sin zona. Con Date.now() a secas y un broker en
   * UTC+2/+3, nunca parecía haber pasado el periodo de la última vela: no se abría la del minuto
   * en curso y los ticks movían la ya cerrada.
   */
  private brokerNowSec(price?: MetaApiPrice): number {
    const realSec = Date.now() / 1000;
    const bt = price?.brokerTime;
    if (bt) {
      const ms = Date.parse((bt.includes('T') ? bt : bt.replace(' ', 'T')) + 'Z');
      // Redondeado a 15 min: el desfase de un broker es de zona horaria, y así no lo mueve la
      // latencia del tick.
      if (Number.isFinite(ms)) this.brokerOffsetSec = Math.round((ms / 1000 - realSec) / 900) * 900;
    }
    return Math.floor(realSec + (this.brokerOffsetSec ?? 0));
  }

  private applyLiveTick(price: MetaApiPrice): void {
    const indices = this.symbolIndexMap[price.symbol.toUpperCase()] || [];
    if (!indices.length) return;

    for (const index of indices) {
      const meta = this.chartMeta[index];
      if (!meta?.candleSeries) continue;

      const candleData: any[] = meta.candleData || [];
      if (!candleData.length) continue;

      const close = price.cotizacion;

      // Si ya pasó el período de la última vela, el tick abre una vela nueva
      // (provisional, marcada `synthetic`) en vez de alterar la ya cerrada.
      // El siguiente refresh la sustituye por la del backend.
      const periodSec = this.getPeriodoIntervalMs(meta.periodo) / 1000;
      const prevBar = candleData[candleData.length - 1];
      const nowSec = this.brokerNowSec(price);
      if (nowSec >= prevBar.time + periodSec) {
        const steps = Math.floor((nowSec - prevBar.time) / periodSec);
        const newBar = { time: prevBar.time + steps * periodSec, open: close, high: close, low: close, close, volume: 0, synthetic: true };
        candleData.push(newBar);
        try {
          meta.candleSeries.update({ time: newBar.time, open: close, high: close, low: close, close });
          meta.volumeSeries?.update({ time: newBar.time, value: 0 });
        } catch (e) {
          console.warn('[multicharts RT] new bar error', e);
        }
      }

      const lastBar = candleData[candleData.length - 1];
      const updatedBar = {
        time:  lastBar.time,
        open:  lastBar.open,
        high:  Math.max(lastBar.high, close),
        low:   Math.min(lastBar.low,  close),
        close
      };
      lastBar.high  = updatedBar.high;
      lastBar.low   = updatedBar.low;
      lastBar.close = close;

      try {
        meta.candleSeries.update(updatedBar);
      } catch (e) {
        console.warn('[multicharts RT] update error', e);
      }

      // Bid price line (green)
      if (meta.bidPriceLine) {
        meta.bidPriceLine.applyOptions({ price: price.bid });
      } else {
        meta.bidPriceLine = meta.candleSeries.createPriceLine({
          price: price.bid,
          color: '#26a69a',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: false,
          title: '',
        });
      }

      // Ask price line (red)
      if (meta.askPriceLine) {
        meta.askPriceLine.applyOptions({ price: price.ask });
      } else {
        meta.askPriceLine = meta.candleSeries.createPriceLine({
          price: price.ask,
          color: '#ef5350',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: false,
          title: '',
        });
      }

      // Actualizar leyenda OHLC superior con el tick en vivo
      const prevClose = candleData.length > 1 ? candleData[candleData.length - 2].close : updatedBar.open;
      meta.legendOhlc = this.buildOhlcState(updatedBar, prevClose);
      meta.legendDate = this.formatTimeLabel(updatedBar.time);
      meta.legendBid = price.bid;
      meta.legendAsk = price.ask;

      // Indicadores de catálogo (cálculo local, barato) siguen al tick; los del
      // backend se recalculan al cerrar vela en refreshCandlesForChart.
      const now = Date.now();
      if (now - (meta.lastLiveIndicatorRecalc ?? 0) >= this.liveIndicatorThrottleMs) {
        meta.lastLiveIndicatorRecalc = now;
        this.refreshCatalogIndicators(index);
        this.updateIndicatorLegendAtTime(index, null);
      }
    }

    this.livePrices[price.symbol.toUpperCase()] = price;
    this.cdr.markForCheck();
  }

  private async createChartForSymbol(container: HTMLDivElement, item: ChartItem, index: number): Promise<void> {
    this.loadingMap[index] = true;
    this.cdr.markForCheck();

    const colors = this.getChartColors();
    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: colors.background },
        textColor: colors.text,
        // Ejes a 10px (por defecto 12): escala de precio más estrecha → más ancho para velas en el mosaico
        fontSize: 10,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: {
        visible: true,
        timeVisible: true,
        secondsVisible: false,
        borderColor: colors.grid,
      },
    });

    chart.applyOptions({
      rightPriceScale: { scaleMargins: { top: 0.12, bottom: 0.02 } },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: colors.upColor,
      downColor: colors.downColor,
      borderVisible: false,
      wickUpColor: colors.upColor,
      wickDownColor: colors.downColor,
    });

    // Volumen (mismo patrón que grafica-modelo: histograma en price scale propio)
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

    this.charts[index] = chart;
    // initialize meta
    this.chartMeta[index] = {
      candleSeries, volumeSeries, indicatorSeries: [], container,
      candleData: [], legendOhlc: null, legendDate: '', legendItems: [],
      periodo: item.periodo ?? 6,
    };

    container.style.position = 'relative';

    // Drawing tools compartidas (mismo controller que grafica-modelo).
    // Se adjunta siempre para que las anotaciones persistidas se vean también en
    // vista de mosaico; el toolbar interactivo solo se muestra al maximizar.
    const drawingCtl = new ChartDrawingController();
    drawingCtl.attach({
      chart,
      series: candleSeries,
      container,
      storageKey: () => item.symbol ? `nectum:drawings:v1:${item.symbol.toUpperCase()}:${item.periodo ?? 6}` : null,
      formatTime: (ms: number) => Math.floor(ms / 1000) as any,
      getBarOHLC: (time: any) => {
        const bar = (this.chartMeta[index]?.candleData || []).find((b: any) => b.time === time);
        return bar ? { open: bar.open, high: bar.high, low: bar.low, close: bar.close } : null;
      },
      requestText: (toolType: string) => this.openDrawingTextDialog(toolType),
      isDark$: this.temaService.valDarkTheme,
    });
    drawingCtl.restoreFromStorage();
    this.chartMeta[index].drawingCtl = drawingCtl;

    // subscribe crosshair moves
    // NOTA: lightweight-charts dispara esto desde sus propios listeners de canvas,
    // que pueden correr fuera de la zona de Angular — sin ngZone.run() el estado
    // se actualiza en memoria pero la vista no se repinta (markForCheck no alcanza
    // si nunca se agenda un ciclo de detección de cambios).
    // Resolución de la vela vía param.time/param.seriesData (igual que grafica-modelo)
    // en vez de coordinateToTime/coordinateToLogical: con paneles de indicadores
    // (osciladores en pane 1) esas coordenadas no siempre mapean a la vela correcta.
    chart.subscribeCrosshairMove((param: any) => {
      this.ngZone.run(() => {
        const meta = this.chartMeta[index];
        if (!meta) return;

        if (!param || !param.time) {
          this.setLastBarLegend(index);
          this.operationHovered.emit([]);
          return;
        }

        const time = param.time;
        const timeKey = this.formatTimeLabel(time);
        const timeDateKey = this.getDateKeyFromCandleTime(time);
        const cd: any = param.seriesData?.get(candleSeries) ?? null;

        // Actualizar leyenda OHLC + indicadores — siempre visible (mosaico y maximizado)
        if (cd && 'open' in cd) {
          const candleData: any[] = meta.candleData || [];
          const idx = candleData.findIndex((d: any) => d.time === time);
          const prevClose = idx > 0 ? candleData[idx - 1].close : cd.open;
          meta.legendOhlc = this.buildOhlcState(cd, prevClose);
        }
        if (timeKey !== 'N/A') meta.legendDate = timeKey;
        this.updateIndicatorLegendAtTime(index, time);
        this.cdr.markForCheck();

        // Resaltado de operaciones sobre la vela — solo al maximizar (evita ruido en mosaico)
        if (this.maximizedIndex !== index) return;

        const hoveredOps = isPeriodoIntraday(meta.periodo ?? 6)
          ? (meta.operations || []).filter((op: any) => this.velaDeOperacion(op, meta.candleData || [], meta.periodo) === time)
          : (meta.operations || []).filter((op: any) => this.getOpDate(op) === timeDateKey);
        const hoveredIsos: string[] = [...new Set<string>(hoveredOps.map((op: any) => op.isooper).filter(Boolean))];
        this.operationHovered.emit(hoveredIsos);
      });
    });

    // Scroll hacia la izquierda: al acercarse a la vela más antigua cargada, pedir las anteriores.
    chart.timeScale().subscribeVisibleLogicalRangeChange((range: any) => {
      if (range) this.maybeLoadOlderCandles(index, range);
    });

    try {
      // Step 1: Call calculo/crear_modelo to get operations, idnectum and indicator data
      let operaciones: any[] = [];
      let idnectum: number | null = null;
      let indicatorResult: any[] = [];
      let periodoModelo = Number(item.periodo ?? 6) || 6;
      // cast_conditions se pide dentro de fetchModelOperations: una sincronización posterior lo deja viejo.
      this.chartMeta[index].indicadoresPedidosMs = Date.now();

      if (item.idModelo && item.fechaRegistroSuscripcion) {
        try {
          const result = await this.fetchModelOperations(item);
          operaciones = result.operaciones;
          idnectum = result.idnectum;
          indicatorResult = result.indicatorResult;
          periodoModelo = Number(result.periodo ?? periodoModelo) || periodoModelo;
          this.setCondicionLegible(index, result.lnList);
          this.chartMeta[index].bajista = result.bajista;
          this.chartMeta[index].castPayload = result.castPayload;
        } catch (opErr) {
          console.warn('Error loading model operations for', item.symbol, opErr);
        }
      }

      this.chartMeta[index].periodo = periodoModelo;

      // Step 2: Fetch candles — use idnectum endpoint when available, fall back to iso
      // Solo las últimas `initialCandles`; las anteriores se piden al hacer scroll (loadOlderCandles).
      const candleUrl = this.buildCandleUrl(item, idnectum, periodoModelo, { limite: this.initialCandles });
      const cacheKey = this.getCandleCacheKey(item, idnectum, periodoModelo);
      this.chartMeta[index].candleSource = { item, idnectum, periodo: periodoModelo };
      const cachedData = this.readCandlesCache(cacheKey);
      if (cachedData.length) {
        this.applyCandlesToChart(index, candleSeries, cachedData, 'default');
      }

      const remoteData = await this.fetchCandlesFromApi(candleUrl);
      const validData = this.mergeCandles(cachedData, remoteData);

      if (validData.length) {
        this.applyCandlesToChart(index, candleSeries, validData, 'default');
        this.writeCandlesCache(cacheKey, validData);
      }

      this.setupCandleAutoRefresh(index, item, idnectum, periodoModelo, candleSeries);

      // Step 3: Overlay operation markers
      if (operaciones.length) {
        this.opsCountMap[item.symbol] = operaciones.length;
        this.chartMeta[index].operations = operaciones;
        this.rebuildOperationMarkers(index);
        this.cdr.markForCheck();
      }

      // Step 4: Overlay indicator lines from cast_conditions result[]
      if (indicatorResult.length) {
        try {
          this.addIndicatorSeries(chart, indicatorResult, item.symbol, index, periodoModelo);
        } catch (indErr) {
          console.warn('Error rendering indicators for', item.symbol, indErr);
        }
        // Las velas ya llegaron completas, pero el broker sincronizó después de calcular los indicadores.
        if (this.chartMeta[index].indicadoresPedidosMs < this.ultimaSincronizacionMs) {
          void this.refreshBackendIndicators(index);
        }
      }
    } catch (err) {
      console.error('Error loading chart for', item.symbol, err);
    } finally {
      this.loadingMap[index] = false;
      this.cdr.markForCheck();
    }
  }

  private async fetchModelOperations(item: ChartItem): Promise<{ operaciones: any[]; idnectum: number | null; indicatorResult: any[]; periodo?: number; lnList: any[]; bajista: boolean; castPayload: any | null }> {
    const empty = { operaciones: [], idnectum: null, indicatorResult: [], periodo: item.periodo, lnList: [], bajista: false, castPayload: null };

    // 1. Load model parameters
    const params: any = await firstValueFrom(
      this._sseService.apiGetNew('parametrosmodelo?idmodelo=' + item.idModelo)
    );
    if (!params || !params.condicionln) return empty;

    const condicionln = JSON.parse(params.condicionln);
    const lnList = params.lnList ? JSON.parse(params.lnList) : [];
    const startDate = (item.fechaRegistroSuscripcion || '').split(' ')[0];
    const horaRegistro = item.fechaRegistroSuscripcion ? (item.fechaRegistroSuscripcion.split(' ')[1] || null) : null;

    const basePayload: any = {
      list_conditions_entry: condicionln.list_conditions_entry || [],
      list_conditions_exit: condicionln.list_conditions_exit || [],
      start: startDate,
      hora: horaRegistro,
      end: new Date().toISOString().split('T')[0],
      idnectums: [],
      stopLoss: condicionln.stopLoss,
      profit: condicionln.profit,
      bajista: condicionln.bajista || false,
      periodo: condicionln.periodo || 6,
      idmercado: condicionln.idmercado,
      sectype: condicionln.sectype || '',
      ln: lnList,
      periodoModeloPeriodico: condicionln.periodoModeloPeriodico ?? -1,
      nocondiciones: condicionln.nocondiciones || false,
      repeticionentradas: condicionln.repeticionentradas || false,
      stopLossActivo: condicionln.stopLossActivo ?? true,
      stopLossDinamicActivo: condicionln.stopLossDinamicActivo ?? true,
      proFitActivo: condicionln.proFitActivo ?? true,
      diasSalidaActivo: condicionln.diasSalidaActivo || false,
      diasSalida: condicionln.diasSalida || 0,
      licencia: this.token,
    };

    // 2. SSE ia/crearmodelojava → operations + idnectum (same payload + metaapi:true)
    const sseResult = await new Promise<{ operaciones: any[]; idnectum: number | null }>((resolve) => {
      const ssePayload = { ...basePayload, metaapi: true };
      const obs = this._sseService.apiGetSse(
        'ia/crearmodelojava?data=', true,
        encodeURIComponent(JSON.stringify(ssePayload))
      );
      let collectedOps: any[] = [];
      let firstIdnectum: number | null = null;
      const sub = obs.subscribe({
        next: (resp: any) => {
          if (resp.name === 'ope' && Array.isArray(resp.data)) {
            collectedOps = collectedOps.concat(resp.data);
            if (firstIdnectum === null && resp.data[0]?.idNectum != null) {
              firstIdnectum = resp.data[0].idNectum;
            }
          }
        },
        complete: () => { sub.unsubscribe(); resolve({ operaciones: collectedOps, idnectum: firstIdnectum }); },
        error: () => { sub.unsubscribe(); resolve({ operaciones: collectedOps, idnectum: firstIdnectum }); },
      });
    });
    const jsonData = {
      idmercado: condicionln.idmercado
    };
    let idnectum: number | null = sseResult.idnectum;
    //if (idnectum === null) {
      try {
        const companias: any = await firstValueFrom(
          this._sseService.apiPostNew('listadecompaniaspormercado', jsonData, this.token)
        );
        console.log(companias);
        if (companias && Array.isArray(companias) && companias.length > 0) {
          // expose all returned companies for UI selection
          item.availableAssets = companias;
          // determine idnectum for currently selected symbol, or default to first
          const currentIso = (item.symbol || '').toUpperCase();
          const match = companias.find((c: any) => (c.iso || '').toUpperCase() === currentIso);
          if (match) {
            idnectum = match.idNectum ?? idnectum;
          } else {
            const idnectumtempora = companias[0].idNectum;
            idnectum = idnectumtempora;
            // only override item.symbol when it is empty or not present in the returned list
            if (!item.symbol || !companias.some((c: any) => c.iso === item.symbol)) {
              item.symbol = companias[0].iso;
            }
          }
          // add all isos for real-time connection
          companias.forEach((c: any) => {
            if (!this.isos.includes(c.iso)) this.isos.push(c.iso);
          });
          console.log(this.isos);
        }
      } catch (e) {
        console.warn('Error fetching listadecompaniaspormercado', e);
      }
    //}

    // 3. POST calculo/cast_conditions → indicator result[] (idnectum required)
    let indicatorResult: any[] = [];
    let castPayload: any = null;
    if (idnectum !== null) {
      try {
        const condicion: string = params.condicion || '';
        castPayload = {
          ...basePayload,
          list_conditions_entry: castEntryConditions(condicion, condicionln.list_conditions_exit),
          list_conditions_exit: [],
          idnectums: [idnectum],
          metaapi: item.metaapi || false,
        };
        delete castPayload.ln;
        indicatorResult = await this.fetchIndicatorResult(castPayload);
        console.log('[multicharts] indicatorResult.length:', indicatorResult.length);
      } catch (e) {
        console.warn('Error fetching indicators for', item.symbol, e);
      }
    }

    // crearmodelojava corre con idnectums: [] → devuelve operaciones de TODO el mercado; el chart
    // solo debe mostrar las del activo cargado (si no, se apilan E/S de otros activos por fecha).
    // Se filtra solo por idNectum; sin idnectum no se muestra ninguna operación.
    console.log('[multicharts] SSE result', {
      symbol: item.symbol,
      idnectum});
    console.log('[multicharts] SSE operaciones sample', sseResult.operaciones);

    const symbolUpper = (item.symbol || '').toUpperCase();
    const operaciones = idnectum != null
      ? sseResult.operaciones.filter((op: any) => Number(op.idNectum) === Number(idnectum))
      : [];

    // Resumen por idNectum para ver qué activos trae el SSE.
    const opsPorIdnectum: Record<string, number> = {};
    sseResult.operaciones.forEach((op: any) => {
      const k = String(op.idNectum);
      opsPorIdnectum[k] = (opsPorIdnectum[k] || 0) + 1;
    });
    console.log('[multicharts] filtro operaciones', {
      symbol: symbolUpper,
      idnectum,
      idnectumSse: sseResult.idnectum,
      total: sseResult.operaciones.length,
      filtradas: operaciones.length,
      descartadas: sseResult.operaciones.length - operaciones.length,
    });
    console.log('[multicharts] operaciones por idNectum:', opsPorIdnectum);
    if (operaciones.length === 0 && sseResult.operaciones.length > 0) {
      console.warn('[multicharts] el filtro descartó todas las operaciones de', symbolUpper, '— muestra:', sseResult.operaciones.slice(0, 3));
    }

    return { operaciones, idnectum, indicatorResult, periodo: condicionln.periodo || item.periodo, lnList, bajista: !!condicionln.bajista, castPayload };
  }

  /** POST calculo/cast_conditions → filas `result[]` con las columnas dsl_* de cada indicador. */
  private async fetchIndicatorResult(castPayload: any): Promise<any[]> {
    const castResponse: any = await firstValueFrom(
      this._sseService.apiPostNew('calculo/cast_conditions', castPayload, this.token)
    );
    if (Array.isArray(castResponse)) {
      // backend returns array of {idnectum, result}
      return castResponse.flatMap((r: any) => r.result || []);
    }
    if (castResponse?.result && Array.isArray(castResponse.result)) {
      return castResponse.result;
    }
    return [];
  }
 
  toggleMaximize(index: number): void {
    this.maximizedIndex = this.maximizedIndex === index ? null : index;
    this.cdr.markForCheck();
    setTimeout(() => this.showLastBars(index), 50);
  }

  onAssetSelected(index: number, symbol: string): void {
    // user changed asset from the select -> reload chart data for that index
    this.reloadChartAtIndex(index, symbol).catch(e => console.warn('Error reloading chart', e));
  }

  private async reloadChartAtIndex(index: number, symbol: string): Promise<void> {
    this.loadingMap[index] = true;
    this.cdr.markForCheck();

    const item = this.data.charts[index];
    if (!item) return;
    item.symbol = symbol;

    // Actualizar symbolIndexMap: quitar index del símbolo anterior y añadirlo al nuevo
    Object.keys(this.symbolIndexMap).forEach(k => {
      this.symbolIndexMap[k] = this.symbolIndexMap[k].filter(i => i !== index);
    });
    const newSym = symbol.toUpperCase();
    if (!this.symbolIndexMap[newSym]) this.symbolIndexMap[newSym] = [];
    if (!this.symbolIndexMap[newSym].includes(index)) this.symbolIndexMap[newSym].push(index);

    // Añadir a isos para realtime si no estaba
    if (!this.isos.includes(newSym)) this.isos.push(newSym);

    // Teardown del chart existente
    try { this.chartMeta[index]?.drawingCtl?.dispose(); } catch {}
    if (this.charts[index]) {
      try { this.charts[index].remove(); } catch {}
      this.charts[index] = null as any;
    }
    if (this.chartMeta[index]) {
      this.chartMeta[index] = null as any;
    }

    this.clearCandleRefreshTimer(index);

    const containers = this.chartContainers.toArray();
    const container = containers[index]?.nativeElement;
    if (!container) {
      this.loadingMap[index] = false;
      this.cdr.markForCheck();
      return;
    }

    // Limpiar DOM residual antes de recrear el chart
    container.innerHTML = '';

    await this.createChartForSymbol(container, item, index);

    // Solo cambia la suscripción si entra un símbolo nuevo o sale uno que ya no tiene gráfica.
    this.connectMetaApiRealTime();

    this.loadingMap[index] = false;
    this.cdr.markForCheck();
  }

  private getOpDate(op: any): string | null {
    if (!op.fechaOperacion) return null;
    const raw = String(op.fechaOperacion);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const ts = Number(raw);
    if (!isNaN(ts) && ts > 0) return new Date(ts).toISOString().slice(0, 10);
    return null;
  }

  /**
   * (Re)dibuja flechas E/S y líneas entrada→salida sobre las velas cargadas. Solo se pueden anclar
   * a velas existentes, así que se vuelve a llamar cuando el scroll carga velas anteriores y
   * aparecen operaciones que antes no tenían vela.
   */
  private rebuildOperationMarkers(index: number): void {
    const meta = this.chartMeta[index];
    const chart = this.charts[index];
    if (!meta?.candleSeries || !chart || !meta.operations?.length) return;

    (meta.operationLineSeries || []).forEach((s: any) => { try { chart.removeSeries(s); } catch {} });
    meta.operationLineSeries = [];

    const { markers, lines } = this.buildOperationMarkers(meta.operations, meta.candleData || [], meta.periodo ?? 6);
    for (const line of lines) {
      try {
        const s = chart.addSeries(LineSeries, {
          color: line.color,
          lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        s.setData(line.data);
        meta.operationLineSeries.push(s);
      } catch {}
    }

    // SL/TP antes que los marcadores: createPriceLine refresca la serie en el acto y, si los
    // marcadores ya están puestos, fija sus índices de barra antes de que addIndicatorSeries añada
    // fechas más antiguas al eje → quedan desplazados fuera de la vista.
    this.applyOpenTradeLinesFor(index);

    try {
      if (meta.markersPlugin) meta.markersPlugin.setMarkers(markers);
      else if (markers.length) meta.markersPlugin = createSeriesMarkers(meta.candleSeries, markers);
    } catch {}
  }

  /**
   * Líneas SL/TP de la última operación si es una ENTRADA sin salida. `stoploss`/`profit` del
   * Operacion del SSE son precios ya actualizados (stop dinámico); los flags vienen de condicionln.
   */
  private applyOpenTradeLinesFor(index: number): void {
    const meta = this.chartMeta[index];
    if (!meta?.candleSeries) return;
    const ops = [...(meta.operations || [])].sort((a: any, b: any) => (this.opVelaSec(a) ?? 0) - (this.opVelaSec(b) ?? 0));
    const last = ops[ops.length - 1];
    const flags = meta.castPayload || {};
    const levels: OpenTradeLevels | null = last?.tipoOperacion === 'ENTRADA'
      ? {
          stoploss: flags.stopLossActivo !== false ? Number(last.stoploss) : null,
          profit: flags.proFitActivo !== false ? Number(last.profit) : null,
        }
      : null;
    meta.openTradeLines = applyOpenTradeLines(meta.candleSeries, meta.openTradeLines || [], levels);
  }

  /**
   * `fechaOperacion` → segundos en el marco de las velas (hora de mercado/broker codificada como UTC).
   * El SSE la manda como instante (Calendar → ms) que el navegador pinta en hora local; se toma esa
   * hora de pared y se codifica como UTC, igual que fechaOperacionAMsVela() de grafica-modelo.
   */
  private opVelaSec(op: any): number | null {
    const raw = op?.fechaOperacion;
    if (raw == null || raw === '') return null;
    let d: Date;
    if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
      const n = Number(raw);
      d = new Date(n > 1e11 ? n : n * 1000);
    } else {
      const s = String(raw).trim().replace(/^(\d{4}-\d{2}-\d{2})[ T]/, '$1T');
      // Sin zona: ya es hora de mercado → directamente como UTC.
      if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/.test(s)) {
        const ms = Date.parse(s.length === 10 ? s + 'T00:00:00Z' : s + 'Z');
        return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
      }
      d = new Date(s);
    }
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()) / 1000);
  }

  /**
   * Vela (time) donde se dibuja la operación. Diario/semanal: primera vela del día (por fecha).
   * Intradía: la vela cuyo intervalo [time, time + periodo) contiene la hora de la operación; si no
   * está cargada, null (se re-dibuja al cargar velas anteriores).
   */
  private velaDeOperacion(op: any, candleData: any[], periodo: number, dateToFirstCandleTime?: Map<string, number>): number | null {
    if (!isPeriodoIntraday(periodo)) {
      const fecha = this.getOpDate(op);
      if (!fecha) return null;
      if (dateToFirstCandleTime) return dateToFirstCandleTime.get(fecha) ?? null;
      const c = candleData.find((v: any) => this.getDateKeyFromCandleTime(v.time) === fecha);
      return c ? c.time : null;
    }
    const sec = this.opVelaSec(op);
    if (sec == null || !candleData.length) return null;
    let lo = 0, hi = candleData.length - 1, found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (candleData[mid].time <= sec) { found = mid; lo = mid + 1; } else hi = mid - 1;
    }
    if (found < 0) return null;
    const t = candleData[found].time;
    return sec < t + this.getPeriodoIntervalMs(periodo) / 1000 ? t : null;
  }

  private buildOperationMarkers(operations: any[], candleData: any[], periodo: number): { markers: any[]; lines: Array<{ color: string; data: any[] }> } {
    const markers: any[] = [];
    const lines: Array<{ color: string; data: any[] }> = [];
    const entradas = operations.filter(op => op.tipoOperacion === 'ENTRADA');
    const salidus  = operations.filter(op => op.tipoOperacion === 'SALIDA');
    const dateToFirstCandleTime = new Map<string, number>();

    (candleData || []).forEach((c: any) => {
      const dateKey = this.getDateKeyFromCandleTime(c.time);
      if (!dateKey) return;
      if (!dateToFirstCandleTime.has(dateKey)) {
        dateToFirstCandleTime.set(dateKey, c.time);
      }
    });

    const opTime = new Map<any, number | null>();
    operations.forEach(op => opTime.set(op, this.velaDeOperacion(op, candleData || [], periodo, dateToFirstCandleTime)));

    operations.forEach(op => {
      const markerTime = opTime.get(op);
      if (markerTime == null) return;
      if (op.tipoOperacion === 'ENTRADA') {
        const price = op.precioentradareal != null ? ` @${(+op.precioentradareal).toFixed(2)}` : '';
        markers.push({ time: markerTime, position: 'belowBar', color: '#26a69a', shape: 'arrowUp', text: `E${price}` });
      } else if (op.tipoOperacion === 'SALIDA') {
        const pnl = op.beneficioperdidapor != null
          ? ` ${(+op.beneficioperdidapor) >= 0 ? '+' : ''}${(+op.beneficioperdidapor).toFixed(1)}%`
          : '';
        const color = op.beneficioperdida != null && (+op.beneficioperdida) >= 0 ? '#26a69a' : '#ef5350';
        markers.push({ time: markerTime, position: 'aboveBar', color, shape: 'arrowDown', text: `S${pnl}` });
      }
    });

    // Draw ENTRADA → SALIDA connecting lines
    const maxPairs = Math.min(entradas.length, salidus.length);
    for (let i = 0; i < maxPairs; i++) {
      const entryTime = opTime.get(entradas[i]);
      const exitTime = opTime.get(salidus[i]);
      const entryPrice = +(entradas[i].precioentradareal ?? entradas[i].cotizacion ?? 0);
      const exitPrice  = +(salidus[i].preciosalidareal  || salidus[i].cotizacion  || 0);
      if (entryTime == null || exitTime == null || !isFinite(entryPrice) || !isFinite(exitPrice) || entryPrice === 0 || exitPrice === 0) continue;
      if (entryTime > exitTime) continue;
      const isProfit = (+salidus[i].beneficioperdida) >= 0;
      lines.push({
        color: isProfit ? 'rgba(38,166,154,0.75)' : 'rgba(239,83,80,0.75)',
        data: [{ time: entryTime, value: entryPrice }, { time: exitTime, value: exitPrice }],
      });
    }

    markers.sort((a, b) => a.time - b.time);
    return { markers, lines };
  }

  private toUnixSeconds(rawTime: any): number | null {
    const n = Number(rawTime);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n > 1e12) return Math.floor(n / 1000);
    if (n > 1e10) return Math.floor(n / 1000);
    return Math.floor(n);
  }

  /**
   * `rango` acota la serie en el backend: `desde`/`hasta` en ms (inclusive) y `limite` = solo las
   * N velas más recientes dentro del rango. Sin rango devuelve la serie completa.
   */
  private buildCandleUrl(item: ChartItem, idnectum: number | null, periodo: number,
                         rango: { desde?: number; hasta?: number; limite?: number } = {}): string {
    let url = idnectum
      ? 'historicos-compania-idnectum?idNectum=' + idnectum + '&metaapi=' + item.metaapi + '&periodo=' + periodo
      : 'historicos-compania?iso=' + encodeURIComponent(item.symbol) + '&metaapi=' + item.metaapi + '&periodo=' + periodo;
    if (rango.desde != null) url += '&desde=' + Math.floor(rango.desde);
    if (rango.hasta != null) url += '&hasta=' + Math.floor(rango.hasta);
    if (rango.limite != null) url += '&limite=' + rango.limite;
    return url;
  }

  private getCandleCacheKey(item: ChartItem, idnectum: number | null, periodo: number): string {
    const brokerTag = item.metaapi ? 'metaapi' : 'default';
    const symbolKey = (item.symbol || '').toUpperCase();
    const scope = idnectum != null ? `idn-${idnectum}` : `iso-${symbolKey}`;
    return `${this.candleCachePrefix}:${brokerTag}:p${periodo}:${scope}`;
  }

  private readCandlesCache(cacheKey: string): any[] {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const candles = Array.isArray(parsed?.candles) ? parsed.candles : [];
      return this.trimCandles(this.normalizeCandleRows(candles));
    } catch {
      return [];
    }
  }

  private writeCandlesCache(cacheKey: string, candles: any[]): void {
    try {
      const payload = {
        updatedAt: Date.now(),
        candles: this.trimCandles(candles)
      };
      localStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch {
      // Ignore quota/storage errors and keep chart data in memory.
    }
  }

  private async fetchCandlesFromApi(candleUrl: string): Promise<any[]> {
    try {
      const API: any = await firstValueFrom(this._sseService.apiGetNew(candleUrl));
      return this.normalizeCandleRows(Array.isArray(API) ? API : []);
    } catch {
      return [];
    }
  }

  private normalizeCandleRows(rows: any[]): any[] {
    const data: any[] = [];
    rows.forEach((row: any) => {
      if (!Array.isArray(row) || row.length < 5) return;
      const candleTime = this.toUnixSeconds(row[0]);
      if (candleTime === null) return;
      const open = Number(row[1]);
      const high = Number(row[2]);
      const low = Number(row[3]);
      const close = Number(row[4]);
      if ([open, high, low, close].some(v => Number.isNaN(v))) return;
      const volume = row.length > 5 ? Number(row[row.length - 1]) : 0;
      data.push({ time: candleTime, open, high, low, close, volume: Number.isFinite(volume) ? volume : 0 });
    });

    data.sort((a, b) => a.time - b.time);
    const dedup = new Map<number, any>();
    data.forEach(c => dedup.set(c.time, c));
    return Array.from(dedup.values()).sort((a, b) => a.time - b.time);
  }

  private mergeCandles(base: any[], incoming: any[]): any[] {
    const merged = new Map<number, any>();
    (base || []).forEach(c => {
      if (c?.time != null) merged.set(c.time, c);
    });
    (incoming || []).forEach(c => {
      if (c?.time != null) merged.set(c.time, c);
    });
    // Sin recortar: en memoria se conservan las velas antiguas cargadas con el scroll.
    // El tope maxCandlesInCache solo aplica a lo que se guarda en localStorage.
    return Array.from(merged.values()).sort((a, b) => a.time - b.time);
  }

  private trimCandles(candles: any[]): any[] {
    if (!Array.isArray(candles)) return [];
    if (candles.length <= this.maxCandlesInCache) return candles;
    return candles.slice(candles.length - this.maxCandlesInCache);
  }

  /**
   * `view`: 'default' encuadra las últimas `defaultVisibleBars` (carga inicial);
   * 'keep' conserva lo que el usuario está mirando (refrescos): si estaba en el
   * borde derecho sigue a las velas nuevas, si no, mantiene las mismas velas.
   */
  private applyCandlesToChart(index: number, candleSeries: any, candles: any[], view: 'default' | 'keep'): void {
    if (!candles.length) return;
    const savedView = view === 'keep' ? this.captureVisibleRange(index, this.chartMeta[index]?.candleData || []) : null;
    const precision = this.inferPricePrecision(candles);
    candleSeries.applyOptions({
      priceFormat: {
        type: 'price',
        precision,
        minMove: 1 / Math.pow(10, precision)
      }
    });
    candleSeries.setData(candles);
    const meta = this.chartMeta[index];
    if (meta) {
      meta.candleData = candles;
      meta.pricePrecision = precision;
      if (meta.volumeSeries) {
        const colors = this.getChartColors();
        const volData = candles.map((c: any) => ({
          time: c.time,
          value: c.volume ?? 0,
          color: c.close >= c.open ? colors.upColor + '88' : colors.downColor + '88',
        }));
        meta.volumeSeries.setData(volData);
      }
    }

    this.setLastBarLegend(index);

    if (view === 'default') {
      // Timeout corto: con autoSize el contenedor puede no tener tamaño aún.
      setTimeout(() => this.showLastBars(index), 50);
    } else if (savedView) {
      this.restoreVisibleRange(index, savedView, candles);
    }
  }

  /**
   * Encuadra las últimas `defaultVisibleBars` velas.
   * OJO: el índice lógico del time scale es la unión de los `time` de TODAS las
   * series, no solo de las velas. Los indicadores del backend traen puntos desde
   * la suscripción (en 1m, días de datos) mientras las velas se recortan a
   * maxCandlesInCache, así que `candleData.length` no sirve como índice: se ancla
   * en la hora de la última vela con timeToIndex.
   */
  private showLastBars(index: number): void {
    const candles: any[] = this.chartMeta[index]?.candleData || [];
    const ts = this.charts[index]?.timeScale?.();
    if (!ts || !candles.length) return;
    try {
      const lastIdx = ts.timeToIndex(candles[candles.length - 1].time, true);
      const firstIdx = ts.timeToIndex(candles[Math.max(0, candles.length - this.defaultVisibleBars)].time, true);
      if (lastIdx == null || firstIdx == null) return;
      ts.setVisibleLogicalRange({ from: firstIdx, to: lastIdx + 3 });
    } catch (e) {
      console.debug('[multicharts] setVisibleLogicalRange failed', e);
    }
  }

  /**
   * Antes de un setData: recuerda el rango lógico visible y una hora de anclaje
   * (la última vela si el usuario estaba en el borde derecho, o la primera hora
   * visible si se había desplazado atrás).
   */
  private captureVisibleRange(index: number, prevCandles: any[]): { range: { from: number; to: number }; anchorTime: any; anchorIdx: number; atRightEdge: boolean } | null {
    const ts = this.charts[index]?.timeScale?.();
    const range = ts?.getVisibleLogicalRange();
    if (!ts || !range || !prevCandles.length) return null;
    const lastIdx = ts.timeToIndex(prevCandles[prevCandles.length - 1].time, true);
    const atRightEdge = lastIdx != null && range.to >= lastIdx;
    const anchorTime = atRightEdge ? prevCandles[prevCandles.length - 1].time : ts.getVisibleRange()?.from;
    const anchorIdx = anchorTime != null ? ts.timeToIndex(anchorTime, true) : null;
    if (anchorIdx == null) return null;
    return { range: { from: range.from, to: range.to }, anchorTime, anchorIdx, atRightEdge };
  }

  /** Tras un setData, desplaza el rango lo que se haya movido el ancla para que la vista no salte. */
  private restoreVisibleRange(index: number, saved: NonNullable<ReturnType<MultichartsDialogComponent['captureVisibleRange']>>, candles: any[]): void {
    const ts = this.charts[index]?.timeScale?.();
    if (!ts || !candles.length) return;
    // En el borde derecho se sigue a la última vela (incluidas las nuevas).
    const anchorTime = saved.atRightEdge ? candles[candles.length - 1].time : saved.anchorTime;
    const newIdx = ts.timeToIndex(anchorTime, true);
    if (newIdx == null) return;
    const shift = newIdx - saved.anchorIdx;
    try {
      ts.setVisibleLogicalRange({ from: saved.range.from + shift, to: saved.range.to + shift });
    } catch {}
  }

  private getPeriodoIntervalMs(periodo: number): number {
    const map: Record<number, number> = {
      1: 60_000,
      2: 5 * 60_000,
      3: 15 * 60_000,
      4: 30 * 60_000,
      5: 60 * 60_000,
      6: 24 * 60 * 60_000,
      7: 4 * 60 * 60_000,
      8: 7 * 24 * 60 * 60_000
    };

    return map[periodo] ?? 60_000;
  }

  private getChartPrecision(index: number): number {
    const p = Number(this.chartMeta?.[index]?.pricePrecision);
    return Number.isFinite(p) && p > 0 ? p : 5;
  }

  private formatPrice(value: number, precision: number): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return n.toFixed(precision);
  }

  private inferPricePrecision(candles: any[]): number {
    if (!Array.isArray(candles) || candles.length === 0) return 5;
    const sample = candles.slice(-200);
    let maxDecimals = 2;

    sample.forEach((c: any) => {
      maxDecimals = Math.max(maxDecimals, this.countDecimals(c?.open));
      maxDecimals = Math.max(maxDecimals, this.countDecimals(c?.high));
      maxDecimals = Math.max(maxDecimals, this.countDecimals(c?.low));
      maxDecimals = Math.max(maxDecimals, this.countDecimals(c?.close));
    });

    return Math.max(2, Math.min(5, maxDecimals));
  }

  private countDecimals(value: any): number {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    const asStr = n.toFixed(8).replace(/0+$/, '');
    const dot = asStr.indexOf('.');
    return dot === -1 ? 0 : asStr.length - dot - 1;
  }

  // ── Leyenda (OHLC + indicadores), como estado — la pinta <app-chart-legend> ──

  private buildOhlcState(bar: { open: number; high: number; low: number; close: number }, prevClose: number): CrosshairOhlc {
    const change = bar.close - prevClose;
    const changePct = prevClose !== 0 ? (change / prevClose) * 100 : null;
    return { open: bar.open, high: bar.high, low: bar.low, close: bar.close, change, changePct };
  }

  /** Restaura la leyenda a la última vela (crosshair fuera del chart, o justo tras cargar datos). */
  private setLastBarLegend(index: number): void {
    const meta = this.chartMeta[index];
    if (!meta) return;
    const candleData: any[] = meta.candleData || [];
    if (!candleData.length) return;
    const last = candleData[candleData.length - 1];
    const prev = candleData.length > 1 ? candleData[candleData.length - 2] : null;
    meta.legendOhlc = this.buildOhlcState(last, prev ? prev.close : last.open);
    meta.legendDate = this.formatTimeLabel(last.time);
    this.updateIndicatorLegendAtTime(index, null);
    this.cdr.markForCheck();
  }

  /** Reconstruye legendItems con el valor en `time` (o el último valor si `time` es null). */
  private updateIndicatorLegendAtTime(index: number, time: number | null | undefined): void {
    const meta = this.chartMeta[index];
    if (!meta) return;
    const precision = this.getChartPrecision(index);
    meta.legendItems = (meta.indicatorSeries || []).map((is: any, i: number): LegendItem => {
      const row = time != null ? (is.data || []).find((d: any) => d.time === time) : null;
      const lastPoint = is.data && is.data.length ? is.data[is.data.length - 1] : null;
      const value = row && isFinite(row.value) ? row.value : (lastPoint ? lastPoint.value : null);
      return {
        id: `ind_${index}_${i}`,
        name: is.label,
        color: is.color,
        value: value != null ? this.formatPrice(Number(value), precision) : '',
        lastValue: lastPoint ? this.formatPrice(Number(lastPoint.value), precision) : '',
      };
    });
  }

  /** Igual que setCondicionLegible() en grafica-modelo: `ln` es un array de tuplas [texto, tipo, meta]. */
  private setCondicionLegible(index: number, ln: any[] | undefined): void {
    const meta = this.chartMeta[index];
    if (!meta) return;
    const { entrada, salida } = splitCondicionLegible(ln);
    meta.condicionEntradaLista = entrada;
    meta.condicionSalidaLista = salida;
  }

  private openDrawingTextDialog(toolType: string): Promise<string | null> {
    const ref = this.dialog.open(DrawingTextDialogComponent, {
      data: { toolLabel: getToolLabel(toolType) },
      autoFocus: true,
    });
    return new Promise(resolve => {
      ref.afterClosed().subscribe((value: string | undefined) => resolve(value === undefined ? null : value));
    });
  }

  private clearCandleRefreshTimer(index: number): void {
    const t = this.candleRefreshTimers[index];
    if (t) clearTimeout(t);
    this.candleRefreshTimers[index] = null;
  }

  /**
   * Programa el siguiente refresco justo después del cierre de la vela en curso
   * (+ refreshGraceMs), en vez de un setInterval arrancado a la hora de carga.
   * Se re-programa tras cada refresh; se corta si el chart se recreó o se destruyó.
   */
  private setupCandleAutoRefresh(index: number, item: ChartItem, idnectum: number | null, periodo: number, candleSeries: any): void {
    this.clearCandleRefreshTimer(index);
    const meta = this.chartMeta[index];
    if (this.destroyed || !meta) return;

    const delay = this.getNextRefreshDelayMs(meta.candleData || [], periodo);
    this.candleRefreshTimers[index] = setTimeout(async () => {
      this.candleRefreshTimers[index] = null;
      try {
        await this.refreshCandlesForChart(index, item, idnectum, periodo, candleSeries);
      } catch (e) {
        console.warn('[multicharts] refresh error', e);
      }
      if (this.chartMeta[index] !== meta) return;
      this.setupCandleAutoRefresh(index, item, idnectum, periodo, candleSeries);
    }, delay);
  }

  /** Ms hasta el próximo cierre de vela, sobre la rejilla anclada en la última vela conocida. */
  private getNextRefreshDelayMs(candles: any[], periodo: number): number {
    const periodMs = this.getPeriodoIntervalMs(periodo);
    const cap = Math.min(periodMs, this.maxRefreshDelayMs);
    const last = candles[candles.length - 1];
    if (!last) return cap;
    const anchorMs = last.time * 1000;
    // Misma base que las velas (hora del broker); con UTC real el refresco no caía en el cierre.
    const now = this.brokerNowSec() * 1000;
    const periods = Math.max(1, Math.ceil((now - anchorMs) / periodMs));
    const delay = anchorMs + periods * periodMs + this.refreshGraceMs - now;
    return Math.max(1000, Math.min(delay, cap));
  }

  private async refreshCandlesForChart(index: number, item: ChartItem, idnectum: number | null, periodo: number, candleSeries: any): Promise<void> {
    const meta = this.chartMeta[index];
    if (!meta?.candleData) return;

    // Incremental: desde la última vela real (inclusive, para recibir su cierre definitivo)
    // en adelante. Sin velas cargadas, las últimas `initialCandles`.
    const lastReal = [...meta.candleData].reverse().find((c: any) => !c.synthetic);
    const url = this.buildCandleUrl(item, idnectum, periodo,
      lastReal ? { desde: lastReal.time * 1000 } : { limite: this.initialCandles });
    const cacheKey = this.getCandleCacheKey(item, idnectum, periodo);
    const incoming = await this.fetchCandlesFromApi(url);
    // El chart pudo recrearse (cambio de activo/timeframe) mientras esperábamos.
    if (this.chartMeta[index] !== meta || !incoming.length) return;

    const prev: any[] = meta.candleData;
    // Las velas `synthetic` (abiertas por ticks) ceden ante las del backend; las posteriores a la
    // última que ya tiene el backend se conservan para que la vela en vivo no desaparezca.
    const real = prev.filter((c: any) => !c.synthetic);
    const realMerged = this.mergeCandles(real, incoming);
    if (!realMerged.length) return;
    const realLast = real[real.length - 1];
    const mergedLast = realMerged[realMerged.length - 1];
    const synthetic = prev.filter((c: any) => c.synthetic);
    const keptSynthetic = synthetic.filter((c: any) => c.time > mergedLast.time);
    const changed = realMerged.length !== real.length
      || mergedLast.time !== realLast?.time
      || mergedLast.close !== realLast?.close
      || keptSynthetic.length !== synthetic.length;

    if (!changed) return;

    const newBar = mergedLast.time !== realLast?.time;

    // La caché solo guarda velas reales: las synthetic no deben sobrevivir a una recarga.
    this.applyCandlesToChart(index, candleSeries, [...realMerged, ...keptSynthetic], 'keep');
    this.writeCandlesCache(cacheKey, realMerged);

    this.refreshCatalogIndicators(index);
    this.updateIndicatorLegendAtTime(index, null);
    this.cdr.markForCheck();

    // Los indicadores del backend cuestan una petición: solo con vela nueva.
    if (newBar) await this.refreshBackendIndicators(index);
  }

  /** El broker acaba de guardar velas MetaAPI: refresca ya los charts MetaAPI sin esperar al timer. */
  private refreshMetaapiChartsNow(): void {
    this.chartMeta.forEach((meta: any, index: number) => {
      const src = meta?.candleSource;
      if (!src?.item?.metaapi || !meta.candleSeries) return;
      // Indicadores aunque no haya vela nueva: pudieron calcularse antes de que el broker rellenara el hueco.
      this.refreshCandlesForChart(index, src.item, src.idnectum, src.periodo, meta.candleSeries)
        .then(() => {
          // Con vela nueva refreshCandlesForChart ya los pidió (y actualizó indicadoresPedidosMs).
          if (this.chartMeta[index] === meta && (meta.indicadoresPedidosMs ?? 0) < this.ultimaSincronizacionMs) {
            return this.refreshBackendIndicators(index);
          }
          return undefined;
        })
        .catch(e => console.warn('[multicharts] refresh tras sincronización', e));
    });
  }

  /**
   * El borde se mide sobre la primera VELA (timeToIndex), no sobre el índice lógico 0: las series
   * de indicadores del backend empiezan antes que las velas y el eje es la unión de todas.
   */
  private maybeLoadOlderCandles(index: number, range: { from: number; to: number }): void {
    const meta = this.chartMeta[index];
    const candles: any[] = meta?.candleData || [];
    if (!meta?.candleSource || meta.olderInFlight || meta.noMoreOlder || !candles.length) return;
    const firstIdx = this.charts[index]?.timeScale?.().timeToIndex(candles[0].time, true);
    if (firstIdx == null || range.from > firstIdx + this.olderLoadThresholdBars) return;
    // Los listeners de lightweight-charts pueden correr fuera de la zona de Angular.
    this.ngZone.run(() => { void this.loadOlderCandles(index); });
  }

  /** Pide las `olderChunkCandles` velas anteriores a la más antigua cargada y las antepone. */
  private async loadOlderCandles(index: number): Promise<void> {
    const meta = this.chartMeta[index];
    if (!meta?.candleSource || meta.olderInFlight || meta.noMoreOlder || !meta.candleData?.length) return;
    const { item, idnectum, periodo } = meta.candleSource;
    const oldestTime: number = meta.candleData[0].time;

    meta.olderInFlight = true;
    try {
      const url = this.buildCandleUrl(item, idnectum, periodo, { hasta: oldestTime * 1000 - 1, limite: this.olderChunkCandles });
      const older = await this.fetchCandlesFromApi(url);
      if (this.chartMeta[index] !== meta) return;

      const prevLen = meta.candleData.length;
      const merged = this.mergeCandles(older, meta.candleData);
      // Menos velas de las pedidas (o ninguna nueva) = principio del histórico.
      if (older.length < this.olderChunkCandles || merged.length === prevLen) meta.noMoreOlder = true;
      if (merged.length === prevLen) return;

      this.applyCandlesToChart(index, meta.candleSeries, merged, 'keep');
      this.refreshCatalogIndicators(index);
      this.rebuildOperationMarkers(index);
      this.updateIndicatorLegendAtTime(index, null);
      this.cdr.markForCheck();
    } finally {
      meta.olderInFlight = false;
    }
  }

  /** Recalcula en cliente las series de catálogo con las velas actuales de `meta.candleData`. */
  private refreshCatalogIndicators(index: number): void {
    const meta = this.chartMeta[index];
    const catalogSeries = (meta?.indicatorSeries || []).filter((s: any) => s.source === 'catalog' && s.entry);
    if (!catalogSeries.length) return;

    const bars = this.toIndicatorBars(meta.candleData || []);
    if (!bars.length) return;
    const plotsByEntry = new Map<IndCatalogEntry, Array<{ label: string; data: any[] }>>();
    for (const s of catalogSeries) {
      let plots = plotsByEntry.get(s.entry);
      if (!plots) {
        try {
          plots = this.computeCatalogPlots(s.entry, bars);
        } catch (e) {
          console.warn('[multicharts] catalog recalc error:', s.id, e);
          plots = [];
        }
        plotsByEntry.set(s.entry, plots);
      }
      const plot = plots.find(p => p.label === s.plotLabel);
      if (!plot) continue;
      try {
        s.series.setData(plot.data);
        s.data = plot.data;
      } catch (e) {
        console.warn('[multicharts] catalog setData error:', s.id, e);
      }
    }
  }

  /** Vuelve a pedir calculo/cast_conditions y reemplaza los datos de las series backend existentes. */
  private async refreshBackendIndicators(index: number): Promise<void> {
    const meta = this.chartMeta[index];
    if (!meta?.castPayload) return;
    if (!(meta.indicatorSeries || []).some((s: any) => s.source === 'backend')) return;
    if (meta.backendIndicatorsLoading) {
      // La petición en vuelo puede no incluir las velas recién guardadas: se repite al terminar.
      meta.backendIndicatorsPendiente = true;
      return;
    }

    meta.backendIndicatorsLoading = true;
    meta.indicadoresPedidosMs = Date.now();
    try {
      const payload = { ...meta.castPayload, end: new Date().toISOString().split('T')[0] };
      const result = await this.fetchIndicatorResult(payload);
      if (this.chartMeta[index] !== meta || !result.length) return;

      for (const s of meta.indicatorSeries) {
        if (s.source !== 'backend') continue;
        const data = this.buildIndicatorSeriesData(result, s.id, meta.periodo);
        if (!data.length) continue;
        try {
          s.series.setData(data);
          s.data = data;
        } catch (e) {
          console.warn('[multicharts] backend indicator setData error:', s.id, e);
        }
      }
      this.updateIndicatorLegendAtTime(index, null);
      this.cdr.markForCheck();
    } catch (e) {
      console.warn('[multicharts] backend indicators refresh error', e);
    } finally {
      meta.backendIndicatorsLoading = false;
      if (meta.backendIndicatorsPendiente && this.chartMeta[index] === meta && !this.destroyed) {
        meta.backendIndicatorsPendiente = false;
        void this.refreshBackendIndicators(index);
      }
    }
  }

  private formatTimeLabel(t: any): string {
    if (t == null) return 'N/A';
    if (typeof t === 'string') return t;
    if (typeof t === 'object' && t.year != null) {
      return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
    }
    if (typeof t === 'number') {
      const d = new Date(t * 1000);
      if (Number.isNaN(d.getTime())) return 'N/A';
      return d.toISOString().slice(0, 16).replace('T', ' ');
    }
    return 'N/A';
  }

  private getDateKeyFromCandleTime(t: any): string | null {
    if (t == null) return null;
    if (typeof t === 'string') return t.slice(0, 10);
    if (typeof t === 'object' && t.year != null) {
      return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
    }
    if (typeof t === 'number') {
      const d = new Date(t * 1000);
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    return null;
  }

  private addIndicatorSeries(chart: any, result: any[], symbol?: string, index?: number, periodo: number = 6): void {
    const palette = this.indicatorPalette;
    const keys = Object.keys(result[0]).filter(k => k.startsWith('dsl_'));
    if (typeof index === 'number' && !this.chartMeta[index]) this.chartMeta[index] = { indicatorSeries: [] };

    // Pass 1: detectar qué grupos (prefijo dsl_X_Y) son oscillators
    // Mismo algoritmo que grafica-modelo: lista explícita + propagación de prefijo cruzado
    const oscillatorPrefixSet = new Set<string>();
    keys.forEach(key => {
      const colonIdx = key.indexOf(':');
      const expr = colonIdx !== -1 ? key.slice(colonIdx + 1) : '';
      const condicionbuscar = expr.replaceAll('#', '').split(':')[0];
      if (!condicionbuscar) return;
      let isOsc = this.oscillators.some(s => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
      if (key.toLowerCase().includes('sma:_')) isOsc = false;
      if (isOsc) oscillatorPrefixSet.add(key.slice(0, colonIdx !== -1 ? colonIdx : key.length));
    });

    // Pass 2: construir mapa grupo → paneIndex con propagación de prefijo
    const groupPaneMap = new Map<string, number>();
    keys.forEach(key => {
      const colonIdx = key.indexOf(':');
      const groupKey = colonIdx !== -1 ? key.slice(0, colonIdx) : key;
      if (groupPaneMap.has(groupKey)) return;
      const expr = colonIdx !== -1 ? key.slice(colonIdx + 1) : '';
      const condicionbuscar = expr.replaceAll('#', '').split(':')[0];
      let isOscillator = false;
      if (condicionbuscar) {
        isOscillator = this.oscillators.some(s => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
        if (key.toLowerCase().includes('sma:_')) isOscillator = false;
        if (!isOscillator && oscillatorPrefixSet.has(groupKey)) isOscillator = true;
      }
      // Fallback: prefijo #price: fuerza overlay
      if (expr.trim().toLowerCase().startsWith('#price:')) isOscillator = false;
      groupPaneMap.set(groupKey, isOscillator ? 1 : 0);
    });

    keys.forEach((key, idx) => {
      const colonIdx = key.indexOf(':');
      const groupKey = colonIdx !== -1 ? key.slice(0, colonIdx) : key;
      const rawLabel = colonIdx !== -1 ? key.slice(colonIdx + 1) : key;
      const displayLabel = rawLabel.replace(/^#[^:]+:/, '');
      const paneIndex = groupPaneMap.get(groupKey) ?? 1;
      const color = palette[idx % palette.length];

      const seriesData = this.buildIndicatorSeriesData(result, key, periodo);
      if (!seriesData.length) return;
      try {
        const seriesOpts: any = {
          color,
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: true,
        };
        if (paneIndex === 0) {
          seriesOpts.priceFormat = { type: 'price', precision: 5, minMove: 0.00001 };
        }
        const s = chart.addSeries(LineSeries, seriesOpts, paneIndex);
        s.setData(seriesData);
        if (paneIndex === 0) {
          // Un operando fuera de magnitud (p.ej. la constante 100 de `#price:_close < 100`)
          // no debe aplastar las velas; si es constante se señala en el borde del panel.
          const candles: any[] = (typeof index === 'number' && this.chartMeta[index]?.candleData) || [];
          fitOverlayToPriceScale(
            s,
            seriesData.map((d: any) => d.value),
            candles.map((c: any) => c.low),
            candles.map((c: any) => c.high),
            color,
          );
        }
        if (typeof index === 'number') {
          this.chartMeta[index].indicatorSeries = this.chartMeta[index].indicatorSeries || [];
          this.chartMeta[index].indicatorSeries.push({ id: key, series: s, data: seriesData, label: displayLabel, color, source: 'backend' });
        }
      } catch (e) { console.warn('[multicharts] addSeries error:', key, e); }
    });

    if (typeof index === 'number') this.updateIndicatorLegendAtTime(index, null);
  }

  /** Columna `key` de las filas de cast_conditions → puntos {time, value} ordenados y sin duplicados. */
  private buildIndicatorSeriesData(result: any[], key: string, periodo: number): any[] {
    const isIntraday = periodo !== 6 && periodo !== 7 && periodo !== 8;
    const rawData = result
      .map((row: any) => {
        const v = row[key];
        const num = typeof v === 'string' ? parseFloat(v) : Number(v);
        // Unix seconds también en D/4H/W (antes 'YYYY-MM-DD'): mismo instante que
        // el business day de lightweight-charts (medianoche UTC), pero comparable
        // con el `time` numérico de las velas en la leyenda del crosshair.
        const raw = String(row.fecha ?? '');
        const ms = Date.parse(isIntraday ? raw : raw.slice(0, 10));
        const time = isFinite(ms) ? Math.floor(ms / 1000) : null;
        return { time, value: num };
      })
      .filter((d: any) => d.time !== null && Number.isFinite(d.value));
    rawData.sort((a: any, b: any) => a.time - b.time);
    return rawData.reduce((acc: any[], d: any) => {
      if (!acc.length || acc[acc.length - 1].time !== d.time) acc.push(d);
      return acc;
    }, []);
  }

  // ── Fase 2: catálogo de indicadores cliente (lightweight-charts-indicators),
  // mismo enfoque que grafica-modelo (addIndicatorToChart), aplicado sobre las
  // velas ya cargadas del chart `index`. Solo visible/usable con la celda maximizada.

  isIndicatorInChart(index: number, id: string): boolean {
    return (this.chartMeta[index]?.indicatorSeries || []).some((e: any) => e.id === id);
  }

  isAddingIndicator(index: number, id: string): boolean {
    return !!this.chartMeta[index]?.addingIndicatorIds?.has(id);
  }

  public addIndicatorToChart(index: number, entry: IndCatalogEntry): void {
    const meta = this.chartMeta[index];
    const chart = this.charts[index];
    if (!meta || !chart) return;
    meta.addingIndicatorIds = meta.addingIndicatorIds || new Set<string>();
    if (meta.addingIndicatorIds.has(entry.id)) return;
    meta.addingIndicatorIds.add(entry.id);
    this.cdr.markForCheck();

    try {
      const bars = this.toIndicatorBars(meta.candleData || []);
      if (!bars.length) return;

      const plotEntries = this.computeCatalogPlots(entry, bars);
      if (!plotEntries.length) return;

      const precision = this.getChartPrecision(index);
      meta.colorIndex = meta.colorIndex ?? 0;
      for (let i = 0; i < plotEntries.length; i++) {
        const { label, data: lineData } = plotEntries[i];

        const color = this.indicatorPalette[meta.colorIndex % this.indicatorPalette.length];
        meta.colorIndex++;
        const seriesId = i === 0 ? entry.id : `${entry.id}_${i}`;
        const title = i === 0 ? entry.short : `${entry.short} · ${label}`;

        const seriesOpts: any = { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false };
        if (entry.overlay) {
          seriesOpts.priceFormat = { type: 'price', precision, minMove: 1 / Math.pow(10, precision) };
        }
        const series = chart.addSeries(LineSeries, seriesOpts, entry.overlay ? 0 : 1);
        series.setData(lineData);

        meta.indicatorSeries = meta.indicatorSeries || [];
        // entry + plotLabel permiten recalcular la serie cuando llegan velas nuevas.
        meta.indicatorSeries.push({ id: seriesId, series, data: lineData, label: title, color, source: 'catalog', entry, plotLabel: label });
      }
      this.updateIndicatorLegendAtTime(index, null);
    } catch (e) {
      console.error('[multicharts] Error adding indicator:', entry.id, e);
    } finally {
      meta.addingIndicatorIds.delete(entry.id);
      this.cdr.markForCheck();
    }
  }

  private toIndicatorBars(candles: any[]): any[] {
    return candles.map((c: any) => ({
      time: c.time, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume,
    }));
  }

  /** Ejecuta el indicador del catálogo y devuelve cada plot como puntos {time, value} válidos. */
  private computeCatalogPlots(entry: IndCatalogEntry, bars: any[]): Array<{ label: string; data: any[] }> {
    const result: any = entry.fn(bars);
    const rawPlots: Array<{ label: string; data: any[] }> = [];
    if (result && typeof result === 'object' && !Array.isArray(result) && result.plots) {
      const metaPlots: any[] = result.metadata?.plots ?? [];
      if (metaPlots.length) {
        for (const pm of metaPlots) {
          const data = result.plots[pm.varName];
          if (Array.isArray(data) && data.length) rawPlots.push({ label: pm.title || pm.varName, data });
        }
      } else {
        const firstKey = Object.keys(result.plots)[0];
        if (firstKey && result.plots[firstKey]?.length) rawPlots.push({ label: entry.short, data: result.plots[firstKey] });
      }
    } else if (Array.isArray(result)) {
      rawPlots.push({ label: entry.short, data: result });
    }
    return rawPlots
      .map(p => ({
        label: p.label,
        data: (p.data || [])
          .filter((d: any) => d && Number.isFinite(d.value))
          .map((d: any) => ({ time: d.time, value: d.value })),
      }))
      .filter(p => p.data.length);
  }

  public removeIndicatorFromChart(index: number, id: string): void {
    const meta = this.chartMeta[index];
    if (!meta) return;
    const entry = (meta.indicatorSeries || []).find((e: any) => e.id === id && e.source === 'catalog');
    if (!entry) return;
    try { this.charts[index]?.removeSeries(entry.series); } catch {}
    meta.indicatorSeries = meta.indicatorSeries.filter((e: any) => e !== entry);
    this.updateIndicatorLegendAtTime(index, null);
    this.cdr.markForCheck();
  }

  // ── Fase 2: timeframe / rangos — misma restricción que "periodoBloqueado" en
  // grafica-modelo: bloqueado cuando el chart viene de un modelo suscrito.

  public setChartPeriodo(index: number, periodo: number): void {
    const item = this.data.charts[index];
    if (!item || this.isChartLocked(index) || item.periodo === periodo) return;
    item.periodo = periodo;
    this.reloadChartAtIndex(index, item.symbol).catch(e => console.warn('Error changing timeframe', e));
  }

  private isChartIntraday(index: number): boolean {
    return isPeriodoIntraday(this.data.charts[index]?.periodo ?? this.chartMeta[index]?.periodo ?? 6);
  }

  public isChartRangeLoading(index: number): boolean {
    return !!this.chartMeta[index]?.rangeLoading;
  }

  /** true si ya se llegó al principio real del histórico (o al tope intradía) para este chart. */
  public isChartRangeExhausted(index: number): boolean {
    return !!this.chartMeta[index]?.noMoreOlder;
  }

  public async setChartRange(index: number, range: string): Promise<void> {
    if (this.isChartLocked(index)) return;
    const chart = this.charts[index];
    const meta = this.chartMeta[index];
    if (!chart || !meta || meta.rangeLoading) return;
    meta.activeRange = range;
    meta.rangeLoadToken = (meta.rangeLoadToken ?? 0) + 1;
    const myToken = meta.rangeLoadToken;
    const isStale = () => this.chartMeta[index] !== meta || meta.rangeLoadToken !== myToken;
    const isIntraday = this.isChartIntraday(index);

    meta.rangeLoading = true;
    this.cdr.markForCheck();
    try {
      if (range === 'All') {
        const cutoffMs = isIntraday ? Date.now() - this.rangeLoadIntradayCapMs : null;
        let guard = 0;
        while (!meta.noMoreOlder && guard++ < this.maxRangeLoadChunks) {
          const oldestMs = meta.candleData?.[0]?.time ? meta.candleData[0].time * 1000 : null;
          if (cutoffMs !== null && oldestMs !== null && oldestMs <= cutoffMs) break;
          if (isStale()) return;
          await this.loadOlderCandles(index);
        }
        if (isStale()) return;
        try { chart.timeScale().fitContent(); } catch {}
        return;
      }
      const now = Date.now();
      const fromMs = rangeFromMs(range, now);
      let guard = 0;
      while (!meta.noMoreOlder && guard++ < this.maxRangeLoadChunks) {
        const oldestMs = meta.candleData?.[0]?.time ? meta.candleData[0].time * 1000 : null;
        if (oldestMs !== null && oldestMs <= fromMs) break;
        if (isStale()) return;
        await this.loadOlderCandles(index);
      }
      if (isStale()) return;
      try {
        chart.timeScale().setVisibleRange({
          from: Math.floor(fromMs / 1000) as any,
          to: Math.floor(now / 1000) as any,
        });
      } catch {}
    } finally {
      meta.rangeLoading = false;
      this.cdr.markForCheck();
    }
  }

  private getChartColors() {
    return getChartColors(this.isDark);
  }

  private applyThemeToCharts(): void {
    const colors = this.getChartColors();
    this.charts.forEach((chart, index) => {
      if (!chart) return;
      chart.applyOptions({
        layout: {
          background: { color: colors.background },
          textColor: colors.text,
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        timeScale: {
          borderColor: colors.grid,
        },
      });
      const meta = this.chartMeta[index];
      if (meta?.candleSeries) {
        meta.candleSeries.applyOptions({
          upColor: colors.upColor,
          downColor: colors.downColor,
          wickUpColor: colors.upColor,
          wickDownColor: colors.downColor,
        });
      }
      if (meta?.volumeSeries && meta.candleData?.length) {
        const volData = meta.candleData.map((c: any) => ({
          time: c.time,
          value: c.volume ?? 0,
          color: c.close >= c.open ? colors.upColor + '88' : colors.downColor + '88',
        }));
        meta.volumeSeries.setData(volData);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.chartMeta.forEach((meta: any) => {
      try { meta?.drawingCtl?.dispose(); } catch {}
    });
    this.charts.forEach(c => { try { c.remove(); } catch {} });
    this.themeSubscription?.unsubscribe();
    this.subRealTimeWs?.unsubscribe();
    this.subRealTimeFallback?.unsubscribe();
    this.subStreamStatus?.unsubscribe();
    this.subVelasSincronizadas?.unsubscribe();
    this.candleRefreshTimers.forEach((_, i) => this.clearCandleRefreshTimer(i));
  }
}
